import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getWebPUrl } from "@/components/ui/optimized-image";

/**
 * Hook to preload the first hero slide image for improved LCP
 * Dynamically injects preload links into the document head
 */
export const useHeroPreload = () => {
  useEffect(() => {
    const preloadHeroImages = async () => {
      try {
        // Fetch first active slide
        const { data } = await supabase
          .from('hero_slides')
          .select('background_image_url, foreground_image_url')
          .eq('active', true)
          .order('display_order', { ascending: true })
          .limit(1)
          .single();

        if (!data?.background_image_url) return;

        const bgUrl = data.background_image_url;
        const webpBgUrl = getWebPUrl(bgUrl);
        
        // Helper to create preload link
        const createPreloadLink = (href: string, type?: string) => {
          // Check if preload already exists
          const existingLink = document.querySelector(`link[rel="preload"][href="${href}"]`);
          if (existingLink) return;

          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = href;
          if (type) link.type = type;
          // @ts-ignore - fetchPriority is valid but not in all TS definitions
          link.fetchPriority = 'high';
          document.head.appendChild(link);
        };

        // Preload WebP version first (for modern browsers)
        if (webpBgUrl) {
          createPreloadLink(webpBgUrl, 'image/webp');
        }
        
        // Preload original as fallback
        createPreloadLink(bgUrl);

        // Also preload foreground image if exists
        if (data.foreground_image_url) {
          const fgUrl = data.foreground_image_url;
          const webpFgUrl = getWebPUrl(fgUrl);
          
          if (webpFgUrl) {
            createPreloadLink(webpFgUrl, 'image/webp');
          }
          createPreloadLink(fgUrl);
        }
      } catch (error) {
        // Silently fail - preload is an optimization, not critical
        console.warn('Failed to preload hero images:', error);
      }
    };

    preloadHeroImages();
  }, []);
};
